import { useIsAdvancedViewMode } from "@/v2-backoffice-client/settings/settings.config"
import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import AppViewSheetBreadcrumb from "@/v2-backoffice-client/app/AppViewSheetBreadcrumb"
import { useTenantViewQuery } from "@/v2-backoffice-client/tenant/view/tenant.view.query"
import TenantViewToolbar from "@/v2-backoffice-client/tenant/view/TenantViewToolbar"
import Tabs from "@/v2-backoffice-client/components/tabs"
import TenantViewInfo from "@/v2-backoffice-client/tenant/view/TenantViewInfo"
import TenantUser from "@/v2-backoffice-client/tenant/user/TenantUser"
import TenantInvitation from "@/v2-backoffice-client/tenant/invitation/TenantInvitation"
import UserLoginPanel from "@/v2-backoffice-client/userLogin/list/UserLoginPanel"
import UsageScopeOverview from "@/v2-backoffice-client/usage/scope/UsageScopeOverview"
import UsageFusionReport from "@/v2-backoffice-client/usage/scope/UsageFusionReport"
import UsageReportSection from "@/v2-backoffice-client/usage/report/UsageReportSection"
import TenantUpdateConfiguration from "@/v2-backoffice-client/tenant/update/TenantUpdateConfiguration"
import TenantPrivacyEditor from "@/v2-backoffice-client/tenant/privacy/TenantPrivacyEditor"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"

function TenantViewSheet() {
  const isAdvanced = useIsAdvancedViewMode()
  const { id } = useAppViewSheetOpener()
  const tenantId = Number(id)
  const { data, loading, error, refetch } = useTenantViewQuery({
    skip: !tenantId,
    variables: {
      id: tenantId!,
    },
  })

  const tenant = data?.getTenantById

  return (
    <>
      <ViewSheet.Header>
        <AppViewSheetBreadcrumb />
        <ViewSheet.Title isLoading={loading}>{tenant?.Name}</ViewSheet.Title>
      </ViewSheet.Header>

      <ViewSheet.HeaderRow>
        <TenantViewInfo tenant={tenant} isLoading={loading} />
        <TenantViewToolbar tenant={tenant} />
      </ViewSheet.HeaderRow>

      <Tabs
        id="tenant-view-tabs"
        defaultValue="users"
        isLoading={loading}
        error={error}
        isEmpty={!tenant}
        emptyLabel="Team not found."
      >
        <Tabs.List>
          <Tabs.Trigger
            value="users"
            isLoading={loading}
            count={(tenant?.UserCount ?? 0) + (tenant?.RemovedUserCount ?? 0)}
          >
            Users
          </Tabs.Trigger>
          <Tabs.Trigger
            value="invitation"
            isLoading={loading}
            count={tenant?.InvitationCount}
          >
            Invitations
          </Tabs.Trigger>
          {/* The badge counts seats active in the last 28 days (the tab's
              headline stat) — not login sessions, which are short-lived and
              read 0 outside business hours. */}
          <Tabs.Trigger
            value="sign-ins"
            isLoading={loading}
            count={tenant?.UsageActiveCount ?? 0}
          >
            Activity
          </Tabs.Trigger>
          <Tabs.Trigger value="access-control">Access Control</Tabs.Trigger>
          <Tabs.Trigger value="api-client">API Client</Tabs.Trigger>
          {isAdvanced && (
            <Tabs.Trigger value="configuration">Configuration</Tabs.Trigger>
          )}
        </Tabs.List>

        <Tabs.Content value="users" className="min-h-0">
          {tenant && (
            <TenantUser
              tenantId={tenant.Id}
              tenantName={tenant.Name}
              activeCount={tenant.UserCount}
              removedCount={tenant.RemovedUserCount}
              onMembershipChanged={() => void refetch()}
            />
          )}
        </Tabs.Content>

        <Tabs.Content value="invitation" className="min-h-0">
          {tenant && <TenantInvitation tenantId={tenant.Id} />}
        </Tabs.Content>

        <Tabs.Content value="sign-ins" className="min-h-0 overflow-auto">
          {tenant && (
            <div className="usage-report">
              <UsageScopeOverview tenantId={tenant.Id} subject={tenant.Name} />
              <UsageFusionReport tenantId={tenant.Id} subject={tenant.Name} />
              <UsageReportSection
                eyebrow="Sign-ins"
                title="Who has signed in recently"
                lead="Active Fusion sessions and recent sign-ins of the team's members, with where they came from."
              >
                <UserLoginPanel
                  tenantId={tenant.Id}
                  tableId="tenant-activity-logins-table"
                />
              </UsageReportSection>
            </div>
          )}
        </Tabs.Content>

        <Tabs.Content value="access-control" className="min-h-0 overflow-auto">
          {/* Keyed by team: AppViewSheet keys the sheet by kind only, so a
              tenant:A -> tenant:B switch keeps this subtree mounted and the
              key is what resets the unsaved draft. */}
          {tenant && (
            <TenantPrivacyEditor key={tenant.Id} tenantId={tenant.Id} />
          )}
        </Tabs.Content>

        <Tabs.Content value="api-client" className="min-h-0">
          <p className="text-sm text-muted-foreground p-4">Coming soon.</p>
        </Tabs.Content>

        {isAdvanced && (
          <Tabs.Content value="configuration" className="flex-1 py-3">
            {tenant && (
              <TenantUpdateConfiguration
                tenantId={tenant.Id}
                configuration={tenant.Configuration}
              />
            )}
          </Tabs.Content>
        )}
      </Tabs>
    </>
  )
}

export default TenantViewSheet
